import { z } from 'zod';
import type { TenantStatus } from './tenancy.contract';

/**
 * Entitlements answer "may this tenant use this capability *right now*".
 *
 * The one rule that matters: business code calls `canAccess(feature)`. It never
 * inspects which modules a tenant purchased. That's a commercial packaging
 * concern that changes with pricing; features are a product concern that
 * does not.
 */
export const FEATURE_KIND = ['BOOLEAN', 'LIMIT', 'QUOTA'] as const;
export type FeatureKind = (typeof FEATURE_KIND)[number];

export const featureDefinitionSchema = z.object({
  key: z
    .string()
    .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/, 'Feature keys are dotted snake_case'),
  moduleKey: z.string(),
  nameKey: z.string(),
  kind: z.enum(FEATURE_KIND),
  /** For LIMIT/QUOTA features: null means unlimited. */
  defaultLimit: z.number().int().nullable().default(null),
});
export type FeatureDefinition = z.infer<typeof featureDefinitionSchema>;

export const SUBSCRIPTION_STATUSES = [
  'TRIALING',
  'ACTIVE',
  'PAST_DUE',
  'GRACE_PERIOD',
  'SUSPENDED',
  'CANCELED',
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export interface EntitlementDecision {
  readonly allowed: boolean;
  readonly featureKey: string;
  /** Present when denied, so the UI can explain and offer the right upgrade. */
  readonly reason: EntitlementDenialReason | null;
  readonly limit: number | null;
  readonly currentUsage: number | null;
}

export const ENTITLEMENT_DENIAL_REASONS = [
  'FEATURE_NOT_IN_PLAN',
  'MODULE_NOT_INSTALLED',
  'MODULE_DISABLED',
  'USAGE_LIMIT_REACHED',
  'SUBSCRIPTION_INACTIVE',
  'FEATURE_UNKNOWN',
] as const;
export type EntitlementDenialReason = (typeof ENTITLEMENT_DENIAL_REASONS)[number];

/**
 * The port every service uses to ask about entitlements.
 *
 * In the current single-process deployment this resolves in-process. When the
 * entitlement service is extracted, the implementation becomes an HTTP client
 * and no caller changes.
 */
export interface EntitlementPort {
  canAccess(tenantId: string, featureKey: string): Promise<EntitlementDecision>;
  canUseModule(tenantId: string, moduleKey: string): Promise<EntitlementDecision>;
  getLimit(tenantId: string, featureKey: string): Promise<number | null>;
  getUsage(tenantId: string, featureKey: string): Promise<number>;
  /** Records consumption against a metered feature. Must be idempotent per key. */
  recordUsage(
    tenantId: string,
    featureKey: string,
    delta: number,
    idempotencyKey: string,
  ): Promise<void>;
  /** Every feature key the tenant currently holds; used to build navigation. */
  listEntitledFeatures(tenantId: string): Promise<readonly string[]>;
}

export const ENTITLEMENT_PORT = Symbol.for('tabita.EntitlementPort');

// --- Platform subscriptions administration -----------------------------------

/**
 * One row of the platform operator's "every subscription" view — a
 * `Tenant` (the subscriber) joined to its `Subscription` (may be null: a
 * tenant mid-registration, before a subscription row exists, is still a
 * real tenant) and the modules currently on that subscription. Owner
 * contact is resolved the same way `administration.contract.ts`'s
 * `MemberResponse` is: through the tenant's owning `TenantMembership`,
 * never a direct FK into identity's schema.
 */
export interface SubscriptionListItemResponse {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly tenantSlug: string;
  readonly tenantStatus: TenantStatus;
  readonly ownerName: string | null;
  readonly ownerEmail: string | null;
  readonly subscriptionStatus: SubscriptionStatus | null;
  readonly trialEndsAt: string | null;
  readonly currentPeriodEnd: string | null;
  readonly canceledAt: string | null;
  readonly moduleKeys: readonly string[];
  readonly createdAt: string;
}

export const listSubscriptionsQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  /** Matches the tenant's name or slug — not the owner's email, which is
   *  resolved from a different schema and shown on each row but not
   *  indexed for search here. */
  search: z.string().trim().max(200).nullish(),
  status: z.enum(SUBSCRIPTION_STATUSES).nullish(),
  moduleKey: z.string().nullish(),
});
export type ListSubscriptionsQuery = z.infer<typeof listSubscriptionsQuerySchema>;
